/**
 * useCalendarNavigation
 *
 * Shared hook that manages calendar navigation state for both
 * CalendarView (homepage) and CareScreen.
 *
 * Encapsulates: displayed month/year, selected day, view mode,
 * navigation handlers, calendar grid generation, and day summaries.
 */

import { useState, useMemo, useCallback } from "react";
import {
  getToday,
  getTodayComponents,
  generateCalendarDays,
  isDayToday,
  toLocalDateString,
  createLocalDateString,
} from "@src/packages/utils/date";
import { useTimezone } from "@src/packages/preferences";
import { useTranslations } from "@src/packages/i18n";
import { useCalendarData } from "../useCalendarData";
import type { DaySummary } from "../calendar.types";
import type { ClinicalDataAvailability } from "../clinical-data-availability";

const MONTH_KEYS = [
  "care.calendar_full_month.january",
  "care.calendar_full_month.february",
  "care.calendar_full_month.march",
  "care.calendar_full_month.april",
  "care.calendar_full_month.may",
  "care.calendar_full_month.june",
  "care.calendar_full_month.july",
  "care.calendar_full_month.august",
  "care.calendar_full_month.september",
  "care.calendar_full_month.october",
  "care.calendar_full_month.november",
  "care.calendar_full_month.december",
];

export interface UseCalendarNavigationOptions {
  /** Initial view mode (default: "monthly") */
  initialViewMode?: "monthly" | "daily";
}

export interface UseCalendarNavigationReturn {
  // State
  displayedYear: number;
  displayedMonth: number;
  selectedDay: Date;
  selectedDayString: string;
  viewMode: "monthly" | "daily";
  setViewMode: (mode: "monthly" | "daily") => void;

  // Derived
  calendarDays: (number | null)[];
  monthYearHeader: string;
  todayString: string;

  // Day helpers
  isToday: (day: number | null) => boolean;
  getDaySummary: (day: number) => DaySummary | undefined;

  // Navigation handlers
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  handleDaySelect: (day: number) => void;

  // Month/year setters (for CareScreen's navigateToDate)
  setDisplayedYear: (year: number) => void;
  setDisplayedMonth: (month: number) => void;
  setSelectedDay: (day: Date) => void;

  // Calendar data pass-through
  daySummaryMap: Map<string, DaySummary>;
  getItemsForDate: (date: string) => import("../calendar.types").CalendarItem[];
  isLoading: boolean;
  isFetching: boolean;
  availability: ClinicalDataAvailability;
  error: Error | null;
  refetch: () => void;
}

export function useCalendarNavigation(
  options?: UseCalendarNavigationOptions,
): UseCalendarNavigationReturn {
  const { initialViewMode = "daily" } = options ?? {};
  const timezone = useTimezone();
  const t = useTranslations();

  // Calendar navigation state
  const { year: initialYear, month: initialMonth } =
    getTodayComponents(timezone);
  const [displayedYear, setDisplayedYear] = useState(initialYear);
  const [displayedMonth, setDisplayedMonth] = useState(initialMonth);

  // View toggle state
  const [viewMode, setViewMode] = useState<"monthly" | "daily">(
    initialViewMode,
  );

  // Daily view - selected day (defaults to today)
  const today = useMemo(() => getToday(timezone), [timezone]);
  const todayString = useMemo(() => toLocalDateString(today), [today]);
  const [selectedDay, setSelectedDay] = useState<Date>(() =>
    getToday(timezone),
  );
  const selectedDayString = useMemo(
    () => toLocalDateString(selectedDay),
    [selectedDay],
  );

  // Fetch calendar data
  const {
    daySummaryMap,
    getItemsForDate,
    isLoading,
    isFetching,
    availability,
    error,
    refetch,
  } = useCalendarData({
    startDate: `${displayedYear}-${String(displayedMonth + 1).padStart(2, "0")}-01`,
    endDate: `${displayedYear}-${String(displayedMonth + 1).padStart(2, "0")}-${String(new Date(displayedYear, displayedMonth + 1, 0).getDate()).padStart(2, "0")}`,
  });

  // Generate calendar grid days
  const calendarDays = useMemo(
    () => generateCalendarDays(displayedYear, displayedMonth),
    [displayedYear, displayedMonth],
  );

  // Month header text — localized
  const monthYearHeader = useMemo(
    () => `${t(MONTH_KEYS[displayedMonth]!)} ${displayedYear}`,
    [displayedYear, displayedMonth, t],
  );

  // Check if a day is today
  const isToday = useCallback(
    (day: number | null) =>
      isDayToday(day, displayedMonth, displayedYear, timezone),
    [displayedMonth, displayedYear, timezone],
  );

  // Get day summary for a given day number
  const getDaySummary = useCallback(
    (day: number): DaySummary | undefined => {
      const dateStr = createLocalDateString(displayedYear, displayedMonth, day);
      return daySummaryMap.get(dateStr);
    },
    [daySummaryMap, displayedYear, displayedMonth],
  );

  // Month navigation
  const goToPreviousMonth = useCallback(() => {
    if (displayedMonth === 0) {
      setDisplayedMonth(11);
      setDisplayedYear((y) => y - 1);
    } else {
      setDisplayedMonth((m) => m - 1);
    }
  }, [displayedMonth]);

  const goToNextMonth = useCallback(() => {
    if (displayedMonth === 11) {
      setDisplayedMonth(0);
      setDisplayedYear((y) => y + 1);
    } else {
      setDisplayedMonth((m) => m + 1);
    }
  }, [displayedMonth]);

  // Day navigation
  const goToPreviousDay = useCallback(() => {
    setSelectedDay((current) => {
      const newDate = new Date(current);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  }, []);

  const goToNextDay = useCallback(() => {
    setSelectedDay((current) => {
      const newDate = new Date(current);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  }, []);

  // Handle day selection from calendar grid
  const handleDaySelect = useCallback(
    (day: number) => {
      const selected = new Date(displayedYear, displayedMonth, day);
      setSelectedDay(selected);
      setViewMode("daily");
    },
    [displayedYear, displayedMonth],
  );

  return {
    displayedYear,
    displayedMonth,
    selectedDay,
    selectedDayString,
    viewMode,
    setViewMode,
    calendarDays,
    monthYearHeader,
    todayString,
    isToday,
    getDaySummary,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousDay,
    goToNextDay,
    handleDaySelect,
    setDisplayedYear,
    setDisplayedMonth,
    setSelectedDay,
    daySummaryMap,
    getItemsForDate,
    isLoading,
    isFetching,
    availability,
    error,
    refetch,
  };
}
